/**
 * .tuMetrics {"tu1": 0.86, "tu2": 0.9, "tu3": 3, "notes": "Agent-created-object protocol: strip the [AGENT_CREATED_OBJECTS:[…]] envelope out of message text (pure) and hydrate the referenced entities into their stores (side effects, one place)", "tuVersion": "2.5"}
 * .help "When an agent creates a card, sprint, plan, etc. from chat, the message carries that object as hidden metadata. This removes the metadata from the visible text and drops the new object into the right list so it appears instantly."
 *
 * Carved out of streamStore (Oct 2026). Previously the extractor existed twice — once in the
 * realtime INSERT handler, once in the fetch/refresh path — with drifting fallback regexes.
 * One implementation now serves both; only the live path hydrates.
 *
 * Tests: node --test frontend/src/store/stream/agentObjects.test.ts  (strip only; hydrate is I/O)
 */

export const AGENT_OBJECTS_MARKER = '[AGENT_CREATED_OBJECTS:[';

export type AgentCreatedObject = { type: string; data: Record<string, unknown> };

/**
 * Requirement/plan rows arrive as raw DB JSON with no mapper of their own yet; the hand-mapping
 * below reads snake_case fields loosely. One deliberate escape hatch, kept here at the boundary.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseRow = Record<string, any>;

/**
 * Remove the `[AGENT_CREATED_OBJECTS:[ … ]]` envelope from `content`. Bracket-counts to find
 * the end so nested JSON is handled; falls back to a lazy regex if the JSON doesn't parse.
 * Returns the cleaned text and the parsed objects (empty when absent or unparsable).
 */
export function stripAgentCreatedObjects(content: string): { content: string; objects: AgentCreatedObject[] } {
  const start = content.indexOf(AGENT_OBJECTS_MARKER);
  if (start === -1) return { content, objects: [] };

  const jsonStart = start + AGENT_OBJECTS_MARKER.length;
  let depth = 1; // we are inside the outer "[" of the array
  let jsonEnd = -1;
  for (let i = jsonStart; i < content.length; i++) {
    const ch = content[i];
    if (ch === '[') depth++;
    else if (ch === ']') {
      depth--;
      if (depth === 0) { jsonEnd = i; break; }
    }
  }

  let objects: AgentCreatedObject[] = [];
  let cleaned: string;

  if (jsonEnd !== -1) {
    try {
      const parsed = JSON.parse('[' + content.substring(jsonStart, jsonEnd) + ']');
      if (Array.isArray(parsed)) objects = parsed.filter((o) => o && typeof o.type === 'string');
    } catch {
      objects = [];
    }
    // jsonEnd is the array's "]"; the envelope's own "]" follows it.
    const after = content[jsonEnd + 1] === ']' ? jsonEnd + 2 : jsonEnd + 1;
    cleaned = (content.substring(0, start).trim() + ' ' + content.substring(after).trim()).trim();
  } else {
    cleaned = content.replace(/\[AGENT_CREATED_OBJECTS:\[[\s\S]*?\]\]/g, '').trim();
  }

  // Sweep any JSON fragments that leaked into the visible text around the envelope.
  cleaned = cleaned
    .replace(/["'][\w_]+["']:\s*(?:null|true|false|"[^"]*"|\d+)[,}\]]*/g, '')
    .replace(/}}]]*/g, '')
    .replace(/\]\]/g, '')
    .trim();

  return { content: cleaned, objects };
}

/* ----------------------------------------------------------------------------------------------
 * Hydration (side effects)
 * --------------------------------------------------------------------------------------------*/

/**
 * Drop agent-created entities into their stores so they appear immediately, the way a
 * user-created card does. Each insert is idempotent (exists-check per type), so it is safe
 * whichever lands first: this optimistic path or the entity's own realtime channel.
 *
 * Everything is imported lazily so this module (and its strip-only tests) never drag the
 * Supabase client or the entity stores into the realtime wiring at load time.
 */
export async function hydrateAgentObjects(objects: AgentCreatedObject[]): Promise<void> {
  if (!objects.length) return;
  const [{ mapCardFromDB }, { default: useCardStore }] = await Promise.all([
    import('../../lib/supabase/queries/card-queries'),
    import('../cardStore'),
  ]);

  for (const obj of objects) {
    try {
      switch (obj.type) {
        case 'card': {
          const card = mapCardFromDB(obj.data as unknown as Parameters<typeof mapCardFromDB>[0]);
          useCardStore.setState((state) =>
            state._allCards.some((c) => c.id === card.id) ? state : { _allCards: [card, ...state._allCards] }
          );
          useCardStore.getState()._applyFilter();
          break;
        }
        case 'sprint': {
          const [{ mapSprintFromDB }, { default: sprintStore }] = await Promise.all([
            import('../../lib/supabase/queries/sprint-queries'),
            import('../sprintStore'),
          ]);
          const sprint = mapSprintFromDB(obj.data as unknown as Parameters<typeof mapSprintFromDB>[0]);
          sprintStore.setState((state) =>
            state.sprints.some((s) => s.id === sprint.id) ? state : { sprints: [sprint, ...state.sprints] }
          );
          break;
        }
        case 'roadmap': {
          const [{ mapRoadmapFromDB }, { default: roadmapStore }] = await Promise.all([
            import('../../lib/supabase/queries/roadmap-queries'),
            import('../roadmapStore'),
          ]);
          const roadmap = mapRoadmapFromDB(obj.data as unknown as Parameters<typeof mapRoadmapFromDB>[0]);
          roadmapStore.setState((state) =>
            state.roadmaps.some((r) => r.id === roadmap.id) ? state : { roadmaps: [roadmap, ...state.roadmaps] }
          );
          break;
        }
        case 'project': {
          const [{ mapProjectFromDB }, { default: projectStore }] = await Promise.all([
            import('../../lib/supabase/queries/project-queries'),
            import('../projectStore'),
          ]);
          const project = mapProjectFromDB(obj.data as unknown as Parameters<typeof mapProjectFromDB>[0]);
          projectStore.setState((state) =>
            state.projects.some((p) => p.id === project.id) ? state : { projects: [project, ...state.projects] }
          );
          break;
        }
        case 'requirement': {
          const { default: requirementStore } = await import('../requirementStore');
          const d = obj.data as LooseRow;
          const requirement = {
            id: d.id,
            createdAt: d.created_at,
            updatedAt: d.updated_at,
            title: d.title,
            description: d.description || undefined,
            status: d.status,
            priority: d.priority as 'low' | 'medium' | 'high',
            version: d.version,
            createdById: d.created_by_id || undefined,
            requestedById: d.requested_by_id || undefined,
            parentId: d.parent_id || undefined,
            tags: d.tags,
            externalLink: d.external_link || undefined,
            acceptanceCriteria: d.acceptance_criteria || undefined,
            businessValue: d.business_value || undefined,
            technicalNotes: d.technical_notes || undefined,
            estimatedEffortDays: d.estimated_effort_days || undefined,
            dependencies: d.dependencies || undefined,
            companyId: d.company_id || undefined,
            teamId: d.team_id || undefined,
            projectId: d.project_id || undefined,
            ticketNumber: d.ticket_number || undefined,
          };
          requirementStore.setState((state) =>
            state.requirements.some((r) => r.id === requirement.id)
              ? state
              : { requirements: [requirement, ...state.requirements] }
          );
          break;
        }
        case 'plan': {
          const { default: planStore } = await import('../planStore');
          const d = obj.data as LooseRow;
          const plan = {
            id: d.id,
            name: d.name,
            description: d.description || undefined,
            status: d.status as 'draft' | 'active' | 'completed' | 'archived',
            version: d.version || '0.0.1',
            team_id: d.team_id || undefined,
            company_id: d.company_id,
            created_by: d.created_by,
            participants: d.participants || [],
            execution_status: d.execution_status || 'not_executed',
            progress_percentage: d.progress_percentage || 0,
            body_markdown: typeof d.body_markdown === 'string' ? d.body_markdown : '',
            plan_settings: d.plan_settings || {},
            related_entity_type: d.related_entity_type || undefined,
            related_entity_id: d.related_entity_id || undefined,
            tags: d.tags || [],
            is_template: d.is_template || false,
            template_category: d.template_category || undefined,
            estimated_duration_days: d.estimated_duration_days || undefined,
            actual_duration_days: d.actual_duration_days || undefined,
            priority: (d.priority as 'low' | 'medium' | 'high' | 'critical') || 'medium',
            editing_by: d.editing_by || undefined,
            editing_since: d.editing_since || undefined,
            created_at: d.created_at,
            updated_at: d.updated_at,
          };
          planStore.setState((state) =>
            state.plans.some((p) => p.id === plan.id) ? state : { plans: [plan, ...state.plans] }
          );
          break;
        }
        default:
          break;
      }
    } catch (e) {
      console.error(`[agentObjects] failed to hydrate ${obj.type}:`, e);
    }
  }
}
